import { createMemo, type JSX } from 'solid-js';

// Circular heat-map dial visualising the recommended activation window
// for a strain. Pure SVG — no canvas, no filters, Capacitor-safe.
//
// The sweet spot is the range between the highest-weighted terpene's
// activation temp and ~10°C above, drawn as a green→orange gradient arc.

export type TempDialProps = {
  minTempC: number;
  maxTempC: number;
  currentTempC?: number;
};

const DIAL_MIN = 100;
const DIAL_MAX = 230;
const RADIUS = 70;
const CENTRE = 80;

function tempToAngle(tempC: number): number {
  const clamped = Math.min(Math.max(tempC, DIAL_MIN), DIAL_MAX);
  const ratio = (clamped - DIAL_MIN) / (DIAL_MAX - DIAL_MIN);
  // Map to 135° … 405° so the dial opens at the bottom.
  return 135 + ratio * 270;
}

function polar(cx: number, cy: number, r: number, angle: number): { x: number; y: number } {
  const rad = ((angle - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function arcPath(from: number, to: number): string {
  const start = polar(CENTRE, CENTRE, RADIUS, from);
  const end = polar(CENTRE, CENTRE, RADIUS, to);
  const largeArc = to - from > 180 ? 1 : 0;
  return `M ${start.x} ${start.y} A ${RADIUS} ${RADIUS} 0 ${largeArc} 1 ${end.x} ${end.y}`;
}

export default function TempDial(props: TempDialProps): JSX.Element {
  const sweetStart = createMemo(() => tempToAngle(props.minTempC));
  const sweetEnd = createMemo(() => tempToAngle(props.maxTempC));
  const pointerAngle = createMemo(() =>
    props.currentTempC ? tempToAngle(props.currentTempC) : null,
  );
  const pointer = createMemo(() => {
    const a = pointerAngle();
    return a == null ? null : polar(CENTRE, CENTRE, RADIUS, a);
  });

  return (
    <svg
      viewBox="0 0 160 160"
      role="img"
      aria-label={`Recommended vape range ${props.minTempC}°C to ${props.maxTempC}°C`}
      class="w-40 h-40"
    >
      <defs>
        <linearGradient id="dial-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="#4ade80" />
          <stop offset="60%" stop-color="#facc15" />
          <stop offset="100%" stop-color="#fb923c" />
        </linearGradient>
      </defs>
      <circle cx={CENTRE} cy={CENTRE} r={RADIUS} fill="none" stroke="#D1E7D0" stroke-width="12" />
      <path
        d={arcPath(sweetStart(), sweetEnd())}
        fill="none"
        stroke="url(#dial-grad)"
        stroke-width="12"
        stroke-linecap="round"
      />
      <text
        x={CENTRE}
        y={CENTRE - 6}
        text-anchor="middle"
        class="font-display fill-[#2D4F1E]"
        font-size="18"
        font-weight="700"
      >
        {props.minTempC}–{props.maxTempC}
      </text>
      <text
        x={CENTRE}
        y={CENTRE + 14}
        text-anchor="middle"
        class="fill-[#2D4F1E]"
        font-size="10"
      >
        °C sweet spot
      </text>
      {pointer() && (
        <circle cx={pointer()!.x} cy={pointer()!.y} r="6" fill="#A78BFA" stroke="white" stroke-width="2" />
      )}
    </svg>
  );
}
